import { useContext, useEffect, useState } from "react";
import { ProductionAndSalesDto } from "../../../../dtos/documents/ProductionAndSales/ProductionAndSalesDto";
import { FormProvider, useForm } from "react-hook-form";
import { NewDocumentModalViewContext } from "../../../Shared/NewDocumentModalView";
import { DocumentModalViewHeader } from "../../../Shared/DocumentModalViewHeader";
import { ProductionAndSalesModel } from "../../../../models/Documents/ProductionAndSales/ProductionAndSalesModel";
import { useSelector } from "react-redux";
import { selectWarehouseId } from "../../../../redux/slices/accountingReportSlice";
import { NFTabs } from "../../../Shared/NewForms/NFTabs";
import { NFTab } from "../../../Shared/NewForms/NFTab";
import { NFTabContainer } from "../../../Shared/NewForms/NFTabContainer";
import useRunError from "../../../Shared/useRunError";
import { useAppSelector } from "../../../../redux/hooks/hooks";
import { selectSubmitCounter } from "../../../../redux/slices/submitCounterSlice";
import handleFormSubmit, { DocumentSaveActionType } from "../../../../helpers/DocumentHelper";
import { AppEntities } from "../../../../config/entities";
import { importFromExcel } from "../../../../helpers/SaveObjectToExcel";
import { saveObjectToExcel } from "../../../../helpers/SaveObjectToExcel";
import ProductionAndSalesDetailSkeleton from "./ProductionAndSalesDetailSkeleton";
import { ProductionAndSalesDetailMain } from "./ProductionAndSalesDetailMain";
import { ProductionAndSalesDetailProducts } from "./ProductionAndSalesDetailProducts";
import { ProductionAndSalesDetailMaterials } from "./ProductionAndSalesDetailMaterials";
import { ProductionAndSalesDetailSalary } from "./ProductionAndSalesDetailSalary";
import { ProductionAndSalesDetailEmployeeEarnings } from "./ProductionAndSalesDetailEmployeeEarnings";
import { ProductionAndSalesDetailCosts } from "./ProductionAndSalesDetailCosts";
import { ProductionAndSalesDetailAdditional } from "./ProductionAndSalesDetailAdditional";
import { ProductionAndSalesDetailAdvances } from "./ProductionAndSalesDetailAdvances";
import { ProductionAndSalesDetailFooter } from "./ProductionAndSalesDetailFooter";
import DocumentDetailViewToolbar from "../../../Shared/DetailViews/DocumentDetailViewToolbar";
import DetailViewPostButton from "../../../Shared/DetailViews/DetailViewPostButton";
import DetailViewSaveButton from "../../../Shared/DetailViews/DetailViewSaveButton";
import DetailViewCancelButton from "../../../Shared/DetailViewCancelButton";
import DetailViewToolbarMoreActions from "../../../Shared/DetailViews/DetailViewToolbarMoreActions";

interface IProps {
  model: ProductionAndSalesDto | null;
  modelUpdatedEvent: (newModel: ProductionAndSalesDto) => void;
}

export const ProductionAndSalesDetail = ({ model, modelUpdatedEvent }: IProps) => {
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors, isValid },
    reset,
    formState,
    getValues,
    watch,
    control,
    trigger,
    ...methods
  } = useForm<ProductionAndSalesModel>();

  const warehouseId = useSelector(selectWarehouseId);

  const modalViewContext = useContext(NewDocumentModalViewContext);
  const { postHandler, saveHandler, closeHandler, cancelHandler } = modalViewContext;
  const watchProducts = watch("products");
  const [tabIndex, setTabIndex] = useState(0);
  const [fileName, setFileName] = useState("Transfer Of Goods");
  const counter = useAppSelector(selectSubmitCounter);
  const runError = useRunError(isValid, errors, counter);

  useEffect(() => {
    console.log(model);
    if (model) {
      setValue("id", model.id);
      setValue("documentNumber", model.documentNumber);
      setValue("documentDate", model.documentDate);
      setValue("createdDate", model.createdDate);
      setValue("customerId", model.customerId);
      setValue("warehouseId", model.warehouseId);
      setValue("divisionId", model.divisionId);
      setValue("currencyId", model.currencyId);
      setValue("currencyRateId", model.currencyRateId);
      setValue("typeOfSale", model.typeOfSale);
      setValue("items", model.items);
      setValue("products", model.products);
      setValue("materials", model.materials);
      setValue("materials", model.materials);
      setValue("salary", model.salary);
      setValue("performedWorks", model.performedWorks);
      setValue("employeeEarnings", model.employeeEarnings);
      setValue("otherCosts", model.otherCosts);
      setValue("otherCosts", model.downPayments);
      setValue("byProducts", model.byProducts);
      setValue("downPayments", model.downPayments);
    }
  }, [model]);

  useEffect(() => {}, [watchProducts]);

  const handleFormSubmitLocal = async (action: DocumentSaveActionType, data: ProductionAndSalesModel) => {
    await handleFormSubmit(action, data, model, saveHandler, postHandler);
  };

  if (!model) {
    return (
      <>
        <ProductionAndSalesDetailSkeleton />
      </>
    );
  }

  const exportToExcel = () => {
    const data = {
      ...model
    };
    const documentInfo = {
      title: AppEntities.ProductionAndSales.title,
      number: model.documentNumber,
      date: model.documentDate
    };
    saveObjectToExcel(data, documentInfo);
  };

  const handleUploadFile = (file: File) => {
    importFromExcel(file, (data: any) => {
      reset(data);
    });
  };
  return (
    <FormProvider
      getValues={getValues}
      setValue={setValue}
      register={register}
      control={control}
      trigger={trigger}
      reset={reset}
      watch={watch}
      handleSubmit={handleSubmit}
      formState={formState}
      {...methods}
    >
      <NFTabs
        onTabChange={(newIndex) => {
          setTabIndex(newIndex);
        }}
      >
        <NFTab title={"Main"} />
        <NFTab title={"Products"} />
        <NFTab title={"Materials"} />
        <NFTab title={"Salary"} />
        <NFTab title={"costs"} />
        <NFTab title={"Additional product"} />
        <NFTab title={"Advances"} />
      </NFTabs>

      <div className={"modal-container-height"}>
        <DocumentDetailViewToolbar>
          <DetailViewPostButton onPost={handleSubmit((data) => handleFormSubmitLocal("post", data))} />
          <DetailViewSaveButton onSave={handleSubmit((data) => handleFormSubmitLocal("save", data))} />
          <DetailViewCancelButton onCancel={cancelHandler} />
          <DetailViewToolbarMoreActions handleUploadFile={handleUploadFile} exportToExcel={exportToExcel} fileName={fileName} setFileName={setFileName} />
        </DocumentDetailViewToolbar>

        <NFTabContainer index={0} currentIndex={tabIndex}>
          <ProductionAndSalesDetailMain />
        </NFTabContainer>

        <NFTabContainer index={1} currentIndex={tabIndex}>
          <ProductionAndSalesDetailProducts />
        </NFTabContainer>

        <NFTabContainer index={2} currentIndex={tabIndex}>
          <ProductionAndSalesDetailMaterials />
        </NFTabContainer>

        <NFTabContainer index={3} currentIndex={tabIndex}>
          <ProductionAndSalesDetailSalary />

          <ProductionAndSalesDetailEmployeeEarnings />
        </NFTabContainer>
        {/* Costs */}
        <NFTabContainer index={4} currentIndex={tabIndex}>
          <ProductionAndSalesDetailCosts />
        </NFTabContainer>
        <NFTabContainer index={5} currentIndex={tabIndex}>
          <ProductionAndSalesDetailAdditional />
        </NFTabContainer>

        <NFTabContainer index={6} currentIndex={tabIndex}>
          <ProductionAndSalesDetailAdvances />
        </NFTabContainer>
      </div>
      <ProductionAndSalesDetailFooter />
    </FormProvider>
  );
};
